/**
 * AdminQuestsTab.jsx — admin-only tab inside the Admin Dashboard for
 * crafting + editing quests. User-spec Feb 26 2026.
 *
 * Form fields:
 *   • Title + description
 *   • Type (read_books / listen_minutes / complete_chapters / streak
 *           / community / plain / secret)
 *   • Target (numeric)
 *   • Tracking mode: auto vs manual (per quest)
 *   • Start at / End at (datetime-local)
 *   • Rewards block — admin picks ANY combination of:
 *       coins / gems / xp / premium_days / achievement_id / custom_text
 *
 * Below the form: list of existing quests with a Delete button.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Trophy, Trash2, Save, PlusCircle } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../utils/authUtils';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_OPTIONS = [
  { v: 'plain', l: 'Plain (mark-complete text quest)' },
  { v: 'read_books', l: 'Read books' },
  { v: 'listen_minutes', l: 'Listen minutes' },
  { v: 'complete_chapters', l: 'Complete chapters' },
  { v: 'streak', l: 'Streak (N days)' },
  { v: 'community', l: 'Community posts' },
  { v: 'secret', l: 'Secret (admin-granted)' },
];

const emptyForm = () => ({
  title: '',
  description: '',
  type: 'plain',
  target: 1,
  tracking_mode: 'manual',
  start_at: '',
  end_at: '',
  rewards: { coins: 0, gems: 0, xp: 0, premium_days: 0, achievement_id: '', custom_text: '' },
});

const AdminQuestsTab = () => {
  const [form, setForm] = useState(emptyForm);
  const [quests, setQuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchQuests = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/quests`);
      if (r.ok) {
        const d = await r.json();
        setQuests(d.quests || []);
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchQuests(); }, [fetchQuests]);

  const updateField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateReward = (k, v) => setForm((f) => ({ ...f, rewards: { ...f.rewards, [k]: v } }));

  const create = async () => {
    if (!form.title.trim()) {
      toast.error('Title required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        type: form.type,
        target: Number(form.target) || 1,
        tracking_mode: form.tracking_mode,
        rewards: form.rewards,
        start_at: form.start_at ? new Date(form.start_at).toISOString() : null,
        end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
      };
      const r = await authFetch(`${BACKEND_URL}/api/quests`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `Failed (${r.status})`);
      toast.success(`Quest "${d.quest.title}" created`);
      setForm(emptyForm());
      fetchQuests();
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  const remove = async (quest_id) => {
    if (!confirm('Delete this quest? User progress will also be cleared.')) return;
    try {
      await authFetch(`${BACKEND_URL}/api/quests/${quest_id}`, { method: 'DELETE' });
      fetchQuests();
    } catch (_) { toast.error('Could not delete'); }
  };

  const inputCls = 'bg-slate-900 border-slate-700 text-white';
  const labelCls = 'text-[11px] uppercase tracking-widest font-bold text-slate-400 mb-1 block';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4" data-testid="admin-quests-tab-content">
      {/* Quest crafter */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <PlusCircle className="w-5 h-5 text-amber-400" /> Craft a Quest
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <label className={labelCls}>Title</label>
            <Input value={form.title} onChange={(e) => updateField('title', e.target.value)} className={inputCls} placeholder="e.g. Read 3 thrillers this week" data-testid="quest-form-title" />
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <textarea
              value={form.description}
              onChange={(e) => updateField('description', e.target.value.slice(0, 2000))}
              rows={3}
              className={`w-full rounded-md ${inputCls} text-sm px-3 py-2`}
              placeholder="What does the reader need to do?"
              data-testid="quest-form-description"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Type</label>
              <select
                value={form.type}
                onChange={(e) => updateField('type', e.target.value)}
                className={`w-full rounded-md ${inputCls} text-sm px-3 py-2`}
                data-testid="quest-form-type"
              >
                {TYPE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Target</label>
              <Input type="number" min={1} value={form.target} onChange={(e) => updateField('target', e.target.value)} className={inputCls} data-testid="quest-form-target" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Tracking</label>
              <select
                value={form.tracking_mode}
                onChange={(e) => updateField('tracking_mode', e.target.value)}
                className={`w-full rounded-md ${inputCls} text-sm px-3 py-2`}
                data-testid="quest-form-tracking"
              >
                <option value="manual">Manual (user taps Mark progress)</option>
                <option value="auto">Auto (system tracks)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Start at</label>
              <Input type="datetime-local" value={form.start_at} onChange={(e) => updateField('start_at', e.target.value)} className={inputCls} data-testid="quest-form-start" />
            </div>
            <div>
              <label className={labelCls}>End at</label>
              <Input type="datetime-local" value={form.end_at} onChange={(e) => updateField('end_at', e.target.value)} className={inputCls} data-testid="quest-form-end" />
            </div>
          </div>

          <div className="pt-2 border-t border-slate-700">
            <div className={labelCls + ' mt-1'}>Rewards (pick any)</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-amber-300 mb-1 block">Coins</label>
                <Input type="number" min={0} value={form.rewards.coins} onChange={(e) => updateReward('coins', Number(e.target.value) || 0)} className={inputCls} data-testid="quest-form-rew-coins" />
              </div>
              <div>
                <label className="text-[10px] text-fuchsia-300 mb-1 block">Gems</label>
                <Input type="number" min={0} value={form.rewards.gems} onChange={(e) => updateReward('gems', Number(e.target.value) || 0)} className={inputCls} data-testid="quest-form-rew-gems" />
              </div>
              <div>
                <label className="text-[10px] text-indigo-300 mb-1 block">XP</label>
                <Input type="number" min={0} value={form.rewards.xp} onChange={(e) => updateReward('xp', Number(e.target.value) || 0)} className={inputCls} data-testid="quest-form-rew-xp" />
              </div>
              <div>
                <label className="text-[10px] text-yellow-300 mb-1 block">Premium days</label>
                <Input type="number" min={0} value={form.rewards.premium_days} onChange={(e) => updateReward('premium_days', Number(e.target.value) || 0)} className={inputCls} data-testid="quest-form-rew-premium" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] text-emerald-300 mb-1 block">Achievement ID (unlocks on claim)</label>
                <Input value={form.rewards.achievement_id} onChange={(e) => updateReward('achievement_id', e.target.value)} className={inputCls} placeholder="e.g. ach_first_quest" data-testid="quest-form-rew-ach" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] text-pink-300 mb-1 block">Custom reward text</label>
                <Input value={form.rewards.custom_text} onChange={(e) => updateReward('custom_text', e.target.value)} className={inputCls} placeholder="e.g. free book signing" data-testid="quest-form-rew-custom" />
              </div>
            </div>
          </div>

          <Button type="button" onClick={create} disabled={submitting} className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold" data-testid="quest-form-submit">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Create Quest
          </Button>
        </CardContent>
      </Card>

      {/* Existing quests */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-400" /> Existing Quests
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading && <div className="text-sm text-slate-400 py-4"><Loader2 className="w-4 h-4 inline-block animate-spin mr-2" /> Loading…</div>}
          {!loading && quests.length === 0 && <div className="text-xs text-slate-500 italic py-2">No quests crafted yet.</div>}
          <ul className="space-y-2 max-h-96 overflow-y-auto">
            {quests.map((q) => (
              <li key={q.quest_id} className="flex items-start gap-2 p-2 rounded-lg bg-slate-900/40 border border-slate-700/50">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-white truncate">{q.title}</div>
                  <div className="text-[11px] text-slate-400">{q.type} · target {q.target} · {q.tracking_mode}</div>
                </div>
                <button type="button" onClick={() => remove(q.quest_id)} className="p-1 rounded hover:bg-slate-700" aria-label="Delete">
                  <Trash2 className="w-4 h-4 text-rose-400" />
                </button>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminQuestsTab;
